import React, { useState, useCallback /*useEffect*/ } from 'react';
import { withRouter } from 'react-router-dom';
import { useQuery } from 'react-query';

import { withStyles } from '@material-ui/core/styles';

import MuiTable from '@material-ui/core/Table';
import MuiTableBody from '@material-ui/core/TableBody';
import MuiTableCell from '@material-ui/core/TableCell';
import MuiTableContainer from '@material-ui/core/TableContainer';
import MuiTableHead from '@material-ui/core/TableHead';
import MuiTableRow from '@material-ui/core/TableRow';
import MuiPaper from '@material-ui/core/Paper';
import MuiCircularProgress from '@material-ui/core/CircularProgress';
import MuiTextField from '@material-ui/core/TextField';
import MuiInputAdornment from '@material-ui/core/InputAdornment';
import MuiSearchIcon from '@material-ui/icons/Search';
import MuiTablePagination from '@material-ui/core/TablePagination';

import _debounce from 'lodash/debounce';

import * as API from 'api';
import Row from './Row';

const Tracks = API.Tracks();

const styles = () => ({
  loadWrapper: {
    display: 'flex',
    justifyContent: 'center',
    margin: '1rem 0',
  },
  actionRow: {
    display: 'flex',
    margin: '1rem 0',
  },
});

const TableReleasePlatformFees = props => {
  const { classes } = props;
  const [searchTitle, setSearchTitle] = useState('');
  const [searchUpc, setSearchUpc] = useState('');
  const [searchIsrc, setSearchIsrc] = useState('');

  const [search, setSearch] = useState({});

  const [pagination, setPagination] = useState({ page_index: 0, page_size: 25 });

  // this is at the table level to make it easier for users to apply the same fee to multiple tracks
  const [newFeePercentage, setNewFeePercentage] = useState();
  const [newFeeStartInclusive, setNewFeeStartInclusive] = useState('-Infinity');
  const [newFeeEndInclusive, setNewFeeEndInclusive] = useState('Infinity');

  const { data, isLoading, isSuccess, refetch } = useQuery(
    ['tracks.search', search, pagination],
    () => Tracks.searchTracks({ search, ...pagination }),
    {
      enabled: !!search,
      refetchOnWindowFocus: false,
      select: response => {
        const tracks = response.results.map(trackResult => ({
          asset_track_id: trackResult.id,
          display_artist_name: trackResult.display_artist_name,
          title: trackResult.title,
          isrc_code: trackResult.isrc_code,
          platformFees: trackResult.platformFees,
          asset_release_upc: trackResult.setTracks
            .map(setTrack => setTrack.releaseSet.release.asset_release_upc)
            .join(', '),
        }));
        return {
          tracks,
          total: response.total,
        };
      },
    }
  );

  function handleChangePage(_, page_index) {
    setPagination(current => ({ ...current, page_index }));
  }

  function handleChangeRowsPerPage({ target: { value } }) {
    setPagination({ page_index: 0, page_size: value });
  }

  const debouncedTriggerSearchTracks = useCallback(
    _debounce((searchTitle, searchUpc, searchIsrc) => {
      setSearch({ searchTitle, searchUpc, searchIsrc });
      setPagination({ page_index: 0, page_size: pagination.page_size });
    }, 350),
    []
  );

  const handleUpdateSearchInput = ({ searchTitle = '', searchUpc = '', searchIsrc = '' }) => {
    setSearchTitle(searchTitle);
    setSearchIsrc(searchIsrc);
    setSearchUpc(searchUpc);

    debouncedTriggerSearchTracks(searchTitle, searchUpc, searchIsrc);
  };

  const tracks = data?.tracks || [];
  const total = data?.total || 0;
  return (
    <>
      <div className={classes.actionRow}>
        <MuiTextField
          variant="outlined"
          label="Match by UPC"
          placeholder="UPC"
          onChange={e => handleUpdateSearchInput({ searchUpc: e.target.value })}
          value={searchUpc}
          InputProps={{
            startAdornment: (
              <MuiInputAdornment position="start">
                <MuiSearchIcon />
              </MuiInputAdornment>
            ),
          }}
        />
        <MuiTextField
          variant="outlined"
          label="Match by ISRC"
          placeholder="ISRC"
          onChange={e => handleUpdateSearchInput({ searchIsrc: e.target.value })}
          value={searchIsrc}
          InputProps={{
            startAdornment: (
              <MuiInputAdornment position="start">
                <MuiSearchIcon />
              </MuiInputAdornment>
            ),
          }}
        />
        <MuiTextField
          variant="outlined"
          label="Search by title"
          placeholder="Track or Release title"
          onChange={e => handleUpdateSearchInput({ searchTitle: e.target.value })}
          value={searchTitle}
          InputProps={{
            startAdornment: (
              <MuiInputAdornment position="start">
                <MuiSearchIcon />
              </MuiInputAdornment>
            ),
          }}
        />
      </div>
      <MuiTableContainer component={MuiPaper}>
        {isLoading ? (
          <div className={classes.loadWrapper}>
            <MuiCircularProgress />
          </div>
        ) : (
          isSuccess &&
          tracks.length > 0 && (
            <>
              <p>
                Results for{' '}
                <strong>
                  {searchUpc}
                  {searchIsrc}
                  {searchTitle}
                </strong>
              </p>
              <MuiTable aria-label="collapsible table">
                <MuiTableHead>
                  <MuiTableRow>
                    <MuiTableCell>id</MuiTableCell>
                    <MuiTableCell>Artist Name</MuiTableCell>
                    <MuiTableCell>isrc</MuiTableCell>
                    <MuiTableCell>Title</MuiTableCell>
                    <MuiTableCell>UPC</MuiTableCell>
                  </MuiTableRow>
                </MuiTableHead>
                <MuiTableBody>
                  <MuiTableRow>
                    <MuiTablePagination
                      rowsPerPageOptions={[25, 50, 100]}
                      rowsPerPage={pagination.page_size}
                      count={total}
                      page={pagination.page_index}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </MuiTableRow>
                  {tracks.map(row => (
                    <Row
                      newFeePercentage={newFeePercentage}
                      setNewFeePercentage={setNewFeePercentage}
                      newFeeStartInclusive={newFeeStartInclusive}
                      setNewFeeStartInclusive={setNewFeeStartInclusive}
                      newFeeEndInclusive={newFeeEndInclusive}
                      setNewFeeEndInclusive={setNewFeeEndInclusive}
                      key={row.asset_track_id}
                      row={row}
                      refetch={refetch}
                    />
                  ))}
                  <MuiTableRow>
                    <MuiTablePagination
                      rowsPerPageOptions={[25, 50, 100]}
                      rowsPerPage={pagination.page_size}
                      count={total}
                      page={pagination.page_index}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </MuiTableRow>
                </MuiTableBody>
              </MuiTable>
            </>
          )
        )}
      </MuiTableContainer>
    </>
  );
};

export default withStyles(styles)(withRouter(TableReleasePlatformFees));
